/**
 * motion.js — Phase 19.1a foundation.
 *
 * Smooth-scroll (Lenis) + GSAP ScrollTrigger, wired for the whole app.
 *
 * Consumers call `initSmoothScroll()` once, from a top-level `useEffect`
 * in App.js. The function is idempotent — repeat calls return the
 * existing instance. `destroySmoothScroll()` is exported for the
 * useEffect cleanup path so hot-reload doesn't leak instances.
 *
 * Reduced-motion policy: if the user prefers reduced motion, `initSmoothScroll`
 * returns null and does NOT create a Lenis instance. Native scroll takes
 * over, ScrollTrigger updates on native scroll events. This is critical
 * for accessibility and for anyone with vestibular sensitivity.
 *
 * GSAP note: only the free `ScrollTrigger` plugin is used. Club GSAP
 * (paid) plugins are off-limits per Phase 19.1a spec.
 */
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

let lenisInstance = null;

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function initSmoothScroll() {
    if (typeof window === "undefined") return null;
    if (prefersReducedMotion()) return null;
    if (lenisInstance) return lenisInstance;

    lenisInstance = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: "vertical",
        smoothWheel: true,
        wheelMultiplier: 1.0,
    });

    lenisInstance.on("scroll", ScrollTrigger.update);
    gsap.ticker.add((time) => lenisInstance.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);

    // Dev-only affordance so the QA acceptance step can confirm Lenis
    // is active from devtools. Never expose in prod builds.
    if (process.env.NODE_ENV !== "production") {
        window.__lenis = lenisInstance;
    }

    return lenisInstance;
}

export function destroySmoothScroll() {
    if (lenisInstance) {
        try {
            lenisInstance.destroy();
        } catch {
            /* noop */
        }
        lenisInstance = null;
        if (process.env.NODE_ENV !== "production") {
            try { delete window.__lenis; } catch { /* noop */ }
        }
    }
}

export function getSmoothScroll() {
    return lenisInstance;
}

/**
 * attachReveals — scan for `.reveal` nodes and wire a GSAP fade-up-on-scroll
 * animation via ScrollTrigger. Idempotent: elements are marked with
 * `data-revealed="1"` after wiring so a re-scan skips them. Respects
 * `prefers-reduced-motion` (see the CSS bypass in /src/styles/tokens.css).
 *
 * 2026-07-27 Round 4 · CHANGE 34 (P0 hotfix) — Failure-safe rewrite.
 *
 * Previous behaviour used `gsap.from(el, { opacity: 0, scrollTrigger: {...} })`
 * which IMMEDIATELY sets opacity=0 on every .reveal target, then waits for
 * ScrollTrigger to detect the trigger point. Under Lenis + GSAP integration
 * timing (Lenis's `on("scroll", ScrollTrigger.update)` doesn't fire an
 * initial refresh that evaluates elements already above-the-fold at page
 * load), the trigger for in-viewport elements never fired → sections stayed
 * permanently at opacity=0, only the page background was visible.
 *
 * New behaviour:
 *   1. Elements render VISIBLE by default via CSS (tokens.css `.reveal`
 *      base rule). No JS-set opacity=0.
 *   2. Only when both (a) motion is allowed AND (b) the element is
 *      OUTSIDE the initial viewport do we hide + fade-in. In-viewport
 *      elements just stay visible — no animation attempt on them.
 *   3. `immediateRender: false` on the fromTo() so a ScrollTrigger
 *      misfire doesn't leave the element hidden.
 *   4. Any element still marked "invisible" is force-restored to visible
 *      by a 3-second safety timer as a final backstop.
 */
export function attachReveals(root) {
    if (typeof document === "undefined") return;
    if (prefersReducedMotion()) return;

    const scope = root || document;
    const targets = scope.querySelectorAll(".reveal:not([data-revealed])");
    const vh = window.innerHeight || 800;
    targets.forEach((el) => {
        el.setAttribute("data-revealed", "1");
        // Above-the-fold elements: skip the fade-in entirely. They stay
        // at their CSS default opacity: 1. This is the critical fix —
        // never hide anything that's already on screen.
        let rect;
        try { rect = el.getBoundingClientRect(); } catch { rect = null; }
        if (!rect || rect.top < vh * 0.9) return;

        // Below-the-fold elements: fade-up-on-scroll. `immediateRender:
        // false` means if ScrollTrigger fails, the element retains its
        // CSS default (visible) rather than being locked at opacity=0.
        gsap.fromTo(el,
            { opacity: 0, y: 24 },
            {
                opacity: 1, y: 0,
                duration: 0.6,
                ease: "power2.out",
                immediateRender: false,
                scrollTrigger: {
                    trigger: el,
                    start: "top 88%",
                    once: true,
                },
            },
        );
    });

    // Multi-pass backstop — 2026-07-29 CHANGE 47 (P0 regression fix) +
    // 2026-08-11 CHG-108 (P0 blank-page fix).
    //
    // The single 3-second sweep from CHANGE 34 was insufficient because it
    // ONLY scanned `.reveal[data-revealed="1"]` elements, which excludes
    // the entire hero timeline (`.hero-eyebrow`, `.hero-word`, `.hero-sub`,
    // `.hero-ctas`, `.hero-trust`, `.hero-floating-card`, `.hl-word`) driven
    // by `gsap.context` in Landing.jsx. When any of those `tl.from(...,
    // { opacity: 0 })` tweens got orphaned mid-play (React StrictMode
    // double-mount, framer-motion route wrapper race, or fast browser
    // reload interrupting the RAF loop), the inline `opacity < 1` persisted
    // and the entire hero stayed invisible — the "front home page content
    // is vanishing" bug.
    //
    // CHG-108 addition: the previous first pass at 1000 ms left a
    // one-second window where a route navigation with in-viewport
    // `.reveal` elements could show a blank viewport while GSAP's
    // ScrollTrigger warmed up. Adding a 120 ms pass closes that window
    // (matches the AnimatePresence route transition duration of 120 ms)
    // so any orphaned opacity:0 lands its restore before the incoming
    // route fully paints. Passes still run at 1 / 3 / 6 / 10 s for the
    // hero timeline / GSAP-context tweens that resolve on a longer
    // schedule.
    const RESTORE_SELECTORS = [
        '.reveal[data-revealed="1"]',
        '.hero-eyebrow',
        '.hero-word',
        '.hero-sub',
        '.hero-ctas',
        '.hero-trust',
        '.hero-floating-card',
        '.hl-word',
    ].join(", ");
    const runBackstop = (label) => {
        let restored = 0;
        scope.querySelectorAll(RESTORE_SELECTORS).forEach((el) => {
            try {
                const cs = window.getComputedStyle(el);
                if (parseFloat(cs.opacity) < 0.9) {
                    el.style.opacity = "1";
                    el.style.transform = "none";
                    restored += 1;
                }
            } catch { /* noop */ }
        });
        if (restored > 0 && process.env.NODE_ENV !== "production") {
            // Dev-only log so QA can confirm the backstop is firing.
            console.log(`[reveals] backstop @${label}ms restored ${restored} element(s)`);
        }
    };
    setTimeout(() => runBackstop(120),    120);
    setTimeout(() => runBackstop(1000),  1000);
    setTimeout(() => runBackstop(3000),  3000);
    setTimeout(() => runBackstop(6000),  6000);
    setTimeout(() => runBackstop(10000), 10000);
}

export { gsap, ScrollTrigger };
